import { type FC, type PropsWithChildren, forwardRef } from 'react';
import { type Checkbox as RadixCheckbox } from 'radix-ui';
import { Check, Loader2, Minus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

type MetadataCheckboxProps = {
  'id': string;
  'name': string;
  'checked': boolean | 'indeterminate';
  'className'?: string;
  'onChange': (value: boolean | 'indeterminate') => void;
  'disabled': boolean;
  'data-cy'?: string;
  'onClick'?: (...args: Array<any>) => void;
  'value'?: boolean;
  'isLoading'?: boolean;
  'aria-invalid'?: boolean | 'true' | 'false';
};

export const MetadataCheckbox: FC<PropsWithChildren<MetadataCheckboxProps>> = forwardRef<
  HTMLDivElement,
  MetadataCheckboxProps
>(({ id, name, checked, value, onChange, 'aria-invalid': ariaInvalid, ...props }, ref) => {
  return (
    <div ref={ref} className="flex">
      <Checkbox
        data-cy={`form-checkbox-${id}`}
        onCheckedChange={onChange as (checked: RadixCheckbox.CheckedState) => void}
        aria-invalid={ariaInvalid}
        {...props}
        className={cn(
          'size-6! rounded-md',
          checked === 'indeterminate'
            ? 'border-input-background bg-status-warning/25 ring-status-warning/50 dark:ring-status-warning-dark/50'
            : 'border-ui-bevel bg-input-background',
          props.className,
        )}
        checked={checked === 'indeterminate' ? 'indeterminate' : value}
      >
        {props.isLoading ? (
          <Loader2 className="icon-4 animate-spin" />
        ) : (
          <>
            <Check className="hidden icon-5 group-data-[state=checked]/checkbox:block" strokeWidth={3} />
            <Minus
              className="hidden icon-6 text-status-warning group-data-[state=indeterminate]/checkbox:block"
              strokeWidth={2}
            />
          </>
        )}
      </Checkbox>
    </div>
  );
});

MetadataCheckbox.displayName = 'MetadataCheckbox';
